import type { ImageScenario } from '../game/imageTypes';

/**
 * Level 2 -- a Jira work item detail view.
 *
 * ============================ DRAFT CLASSIFICATION ==========================
 * Regions are measured by tools/find_arrows.py and should be correct. Which
 * arrows are real defects is a draft and needs confirming before release.
 * ============================================================================
 *
 * Image is 2000 x 1333. All regions are percentages of those dimensions.
 */
export const workItem: ImageScenario = {
  id: 'jira-work-item',
  title: 'A critical story nobody can start',
  subtitle: 'Work item detail · KD-3',
  brief:
    'A story marked Critical, sitting in the backlog. Ten arrows point at fields somebody thought were worth a second look. Several of those fields are empty for perfectly good reasons — decide which emptiness actually matters.',

  source: {
    toolId: 'jira',
    screen: 'Work item detail view',
    kind: 'screenshot',
    note: 'Captured from a ReleaseTEAM test instance, annotated with red arrows.',
  },

  image: {
    src: '/scenarios/work-item.webp',
    width: 2000,
    height: 1333,
    alt: 'Jira work item KD-3 titled "test resolve", status Backlog, priority Critical, unassigned, with an empty description, no subtasks, no labels, no due date, an original estimate of 0m and no start date.',
  },

  arrows: [
    {
      id: 'add-parent',
      label: '1',
      region: { x: 3.75, y: 4.89, w: 3.0, h: 5.0 },
      description: 'Arrow pointing at the "Add parent" link above the title',
      decoyNote:
        'The item has no parent epic, which the "Add parent" affordance is simply offering to fix. Plenty of legitimate work is standalone — a bug fix or a piece of tech debt does not need to belong to an epic — so an unparented story is a roadmap preference, not a misconfiguration.',
    },
    {
      id: 'restrictions-lock',
      label: '2',
      region: { x: 75.47, y: 5.56, w: 3.0, h: 5.0 },
      description: 'Arrow pointing at the padlock icon in the toolbar',
      decoyNote:
        'The padlock is drawn open, which means no issue restrictions are applied and the whole team can see this item. A closed padlock on a Critical story would be worth investigating; an open one is the normal, healthy state.',
    },
    {
      id: 'empty-description',
      label: '3',
      region: { x: 13.7, y: 21.88, w: 7.5, h: 5.0 },
      description: 'Arrow pointing at the empty Description field',
    },
    {
      id: 'unassigned',
      label: '4',
      region: { x: 88.35, y: 22.63, w: 3.65, h: 5.0 },
      description: 'Arrow pointing at the Assignee field reading Unassigned',
    },
    {
      id: 'no-subtasks',
      label: '5',
      region: { x: 11.0, y: 31.33, w: 6.9, h: 5.0 },
      description: 'Arrow pointing at the empty Subtasks section',
      decoyNote:
        'Subtasks are optional and only earn their keep when work genuinely needs splitting between people. A story with none is not incomplete — teams that mandate subtasks on everything usually end up with a lot of one-line subtasks nobody updates.',
    },
    {
      id: 'labels-none',
      label: '6',
      region: { x: 82.2, y: 35.87, w: 5.4, h: 5.0 },
      description: 'Arrow pointing at the Labels field reading None',
      decoyNote:
        'Labels are an optional, free-text categorisation. An unlabelled item is not broken, and teams that do not run label-driven filters gain nothing by inventing one. Empty here means unused, not misconfigured.',
    },
    {
      id: 'due-date-none',
      label: '7',
      region: { x: 82.0, y: 41.5, w: 5.9, h: 5.0 },
      description: 'Arrow pointing at the Due date field reading None',
      decoyNote:
        'Most agile teams never set due dates on stories — the sprint or the release carries the date, and a per-item deadline just creates noise in overdue reports. An empty due date on a backlog item is entirely normal.',
    },
    {
      id: 'estimate-zero',
      label: '8',
      region: { x: 81.9, y: 47.69, w: 5.65, h: 5.0 },
      description: 'Arrow pointing at the Original estimate field reading 0m',
    },
    {
      id: 'start-date-none',
      label: '9',
      region: { x: 83.8, y: 52.98, w: 5.35, h: 5.0 },
      description: 'Arrow pointing at the Start date field reading None',
      decoyNote:
        'The item is still in the backlog, so it has not started — an empty start date is simply the truth. It would only be a problem on something already in progress.',
    },
    {
      id: 'never-updated',
      label: '10',
      region: { x: 70.45, y: 90.32, w: 3.45, h: 5.78 },
      description: 'Arrow pointing at the Created and Updated timestamps, which match',
    },
  ],

  defects: [
    {
      id: 'd-empty-description',
      arrowId: 'empty-description',
      title: 'Critical story with no description',
      severity: 'major',
      explanation:
        'The description is empty on an item marked Critical, and the summary — "test resolve" — says nothing either. Nobody can pick this up, size it, or challenge whether it really is critical, because the only person who knows what it means is the reporter. The rest of the fields being blank is a symptom; this is the cause.',
      fix: 'Write down the problem, the expected behaviour, and how anyone would know it is finished. If that cannot be articulated, the item is not ready to be Critical and should not be carrying that priority.',
    },
    {
      id: 'd-critical-unassigned',
      arrowId: 'unassigned',
      title: 'Critical priority with nobody accountable',
      severity: 'major',
      explanation:
        'Priority is Critical, yet there is no assignee and the item is sitting in the Backlog. Criticality is a claim about urgency, and an urgent item with no owner is a claim nobody has to answer for. In practice this is how a "critical" backlog grows into dozens of items that everyone has learned to ignore.',
      fix: 'Either give it an owner and pull it into the active sprint, or lower the priority to something honest. The one thing not to do is leave it Critical and unowned.',
    },
    {
      id: 'd-estimate-zero',
      arrowId: 'estimate-zero',
      title: 'Original estimate of 0m, not simply blank',
      severity: 'major',
      explanation:
        'The estimate reads 0m rather than being empty, and that distinction matters. An empty estimate is visibly missing and shows up in "unestimated work" reports. An explicit zero asserts the work is free: it passes every completeness check, contributes nothing to capacity, and quietly makes the sprint look emptier than it is.',
      fix: 'Clear the field so the item registers as unestimated, then estimate it properly during refinement. A zero should only ever mean genuinely no effort.',
    },
    {
      id: 'd-never-updated',
      arrowId: 'never-updated',
      title: 'Created and never touched since',
      severity: 'minor',
      explanation:
        'Created and Updated carry the identical timestamp, so nothing has happened to this item since the moment it was filed — no triage, no refinement, no comment. For an item claiming Critical priority, that is the clearest evidence yet that the priority is not real.',
      fix: 'Triage it. Anything that has sat untouched since creation while flagged Critical should be re-prioritised or closed, and a saved filter on "Critical and not updated in 30 days" will stop the next one hiding.',
    },
  ],
};
